import PropTypes from "prop-types"
import React, { useMemo } from "react"

import moment from "moment"
import WeekGrid from "./WeekGrid"
import CrewWeekGrid from "./CrewWeekGrid"
import EquipmentWeekGrid from "./EquipmentWeekGrid"
import DayGrid from "./DayGrid"
import ListViewGrid from "./ListViewGrid"
import { DEFAULT_END_HOUR, DEFAULT_START_HOUR } from "../../constants"
import { useDevice } from "hooks/useDevice"
import useSearchFilter from "hooks/useSearchFilter"

const CalendarTimeGrid = ({ date, events = [], type, resources, agenda, activeTab, crewIds, equipmentIds, categoryIds, roleIds, onChangeFilters, startHour = DEFAULT_START_HOUR, endHour = DEFAULT_END_HOUR }) => {
  const hours = useMemo(
    () => Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i),
    [startHour, endHour]
  )
  const weekStartDate = useMemo(() => moment(date).clone().day(0).startOf("day"), [date])
  const weekEndDate = useMemo(() => moment(date).clone().day(6).startOf("day"), [date])
  const { isMobile, isTablet } = useDevice()
  const isResponsive = isMobile || isTablet
  const [params] = useSearchFilter({ accessAttributes: ["listview"] })
  const isExplicitListView = params.listview === true || params.listview === "true"

  const calendarDaysCount = useMemo(() => weekEndDate.diff(weekStartDate, "days") + 1, [weekStartDate, weekEndDate])
  const calendarDays = useMemo(
    () => new Array(calendarDaysCount).fill().map((_, index) => weekStartDate.clone().add(index, "days")),
    [weekStartDate, calendarDaysCount]
  )

  // List view only when user explicitly selects "List View" on mobile/tablet
  if (type === "week" && isExplicitListView && isResponsive) {
    return <ListViewGrid events={events} activeTab={activeTab} crewIds={crewIds} equipmentIds={equipmentIds} categoryIds={categoryIds} roleIds={roleIds} onChangeFilters={onChangeFilters} />
  }

  // Crew/Equipment tabs never use agenda (time-grid) view
  if (type === "week" && activeTab === 'crew') {
    return <CrewWeekGrid calendarDays={calendarDays} events={events} crewIds={crewIds} roleIds={roleIds} />;
  }
  if (type === "week" && activeTab === 'equipment') {
    return <EquipmentWeekGrid calendarDays={calendarDays} events={events} equipmentIds={equipmentIds} categoryIds={categoryIds} />;
  }
  if (type === "week") {
    const myBookingsEmpty = activeTab === 'my-bookings' && events.length === 0
    const myBookingsHours = Array.from({ length: 13 }, (_, i) => i)
    // Agenda view must not show when list view mode is active (mobile/web)
    const showAgenda = activeTab === 'my-bookings' ? true : (agenda && !isExplicitListView)
    const gridHours = myBookingsEmpty ? myBookingsHours : hours
    return <WeekGrid calendarDays={calendarDays} hours={gridHours} events={events} agenda={showAgenda} startHour={startHour} endHour={endHour} />
  }
  return (
    <DayGrid date={date} calendarDays={calendarDays} hours={hours} events={events} resources={resources} profileIds={crewIds} boatIds={equipmentIds} activeTab={activeTab} startHour={startHour} endHour={endHour} />
  )
}

CalendarTimeGrid.propTypes = {
  type: PropTypes.string,
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  resources: PropTypes.array,
  agenda: PropTypes.bool,
  activeTab: PropTypes.string,
  crewIds: PropTypes.array,
  equipmentIds: PropTypes.array,
  categoryIds: PropTypes.array,
  roleIds: PropTypes.array,
  onChangeFilters: PropTypes.func,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      start: PropTypes.number,
      end: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string
    })
  ),
  startHour: PropTypes.number,
  endHour: PropTypes.number,
}

export default CalendarTimeGrid
